import React from 'react';
import { defineMessages } from 'react-intl';
import { intl } from 'hellospa/components/hs-intl-provider';
import { trackHeapCustomEvent, RelayHeapEvents } from 'common/utils/heap';
import { useWindowMessage, MessageType } from 'common/utils/ts-message';
import { makeContextCacher, makeShallowCache, shallowEqual } from 'common/utils/context';
import debounce from 'lodash.debounce';
import uuid from 'uuid/v4';
import {
  sortRules, isSorted, validate as validateRules, cleanupRules, getFieldIdsFromRules,
} from 'hellospa/conditional-logic';
import {
  SignatureRequestContext, PREPARER, SENDER, FIT_WIDTH,
} from 'hellospa/components/signature-request';
import { UserFileTypes, FilePollResponseStatus } from 'hellospa/page/prep-and-send/data/types/file';
import { IntegrationContext, IntegrationReturnToLabel } from 'hellospa/components/integration/data/types';
import fieldOperations, { makeFieldHistory } from '../field-operations';
import { nudge } from '../drag-layer/resize-field';
import ConfirmModal from './confirm-modal';


import * as rulesPanel from '../rules-panel';

import {
  hasFeature,
  IS_MAC,
  FEATURE_SENDER_ASSIGN,
  FEATURE_DATE_FORMAT,
  FEATURE_ROTATE_PAGES,
  RADIO_SPACING,
  FEATURE_CONDITIONAL_LOGIC_NESTED,
  FEATURE_EMBED,
  FEATURE_TEMPLATE_ONE_OFF_FILE,
  FEATURE_TEMPLATE_ROLES,
  ASSIGNED_TO_RIGHT,
  ASSIGNED_TO_LEFT,
  FEATURE_EDITOR_MOBILE,
  FEATURE_ADVANCED_MERGE_FIELD,
} from '../constants';

export const editorContext = React.createContext();

export {
  PREPARER, SENDER, hasFeature, FEATURE_DATE_FORMAT,
};

const COPY_TYPE = 'text/plain';
const COPY_TYPE_IE = 'Text';

const cleanField = (fieldData) => {
  const copy = { ...fieldData };
  delete copy.signature;
  delete copy.id;
  delete copy.invalidApiId;
  delete copy.isEditableMergeField;
  if (copy.signer !== PREPARER) {
    delete copy.value;
  }
  return copy;
};

const documentCache = {};
const findDocumentById = (documents, documentId) => {
  if (documentId in documentCache) {
    return documentCache[documentId];
  }

  const found = documents.find(
    (document) => document.id === documentId,
  );

  if (found) {
    documentCache[documentId] = found;
  }

  return found;
};


function RemoteEditorValidation({ isUploading, sendEditorMessage }) {
  const { showRulesPanel, liveValidationErrors, fields } = React.useContext(editorContext);

  let errorMessage;
  let isValid = !showRulesPanel;
  const isBackDisabled = isUploading;

  if (liveValidationErrors) {
    isValid = false;
    errorMessage = liveValidationErrors[0].message;
  }
  const numFields = fields.length;

  if (isUploading) {
    isValid = false;
  }

  React.useEffect(() => {
    // is CL rule panel is open, disable next button
    sendEditorMessage({ isValid, errorMessage, numFields, isBackDisabled });
  }, [isValid, errorMessage, numFields, sendEditorMessage, showRulesPanel, isBackDisabled]);
  return null;
}

function RemoteContinue({ onContinue }) {
  useWindowMessage(MessageType.EditorContinue, onContinue);
  return null;
}

function RemoteAutoSave({ autoSave }) {
  useWindowMessage(MessageType.EditorAutoSave, autoSave);
  return null;
}

const selectedCache = new WeakMap();

const messages = defineMessages({
  nameMeNow: {
    id: '', // IDs are procedurally generated
    description: 'An assignment for a field that must be filled out by the preparing user now.',
    defaultMessage: 'Me (now)',
  },
  stillUploading: {
    id: '',
    description: 'Validation that prevents users from leaving while a file is uploading',
    defaultMessage: 'A file is still uploading',
  },
  nameMeWhenSending: {
    id: '',
    description: 'An assigment for a field that will be filled out by the preparing user when sending.',
    defaultMessage: 'Me (when sending)',
  },
  nameSender: {
    id: '',
    description: 'An assigment for a field that will be filled out by the document sender.',
    defaultMessage: 'Sender',
  },
  nameAppNameWhenSending: {
    id: '',
    description: 'An assigment for a field that will be filled out by the sending application when sending the document.',
    defaultMessage: '{appName} (when sending)',
  },
  cancelWarningMsg: {
    id: '',
    description: 'warning message when user tries to canel signature request while editing in editor',
    defaultMessage: 'Are you sure you want to cancel? You will lose all of your unsaved changes.',
  },
  deleteRuleHeader: {
    id: '',
    description: 'header text of delete modal in editor appears when trying to delete input field that is part of conditional rule',
    defaultMessage: 'Delete rule',
  },
  deleteRuleMessage: {
    id: '',
    description: 'message in assigned-to modal in editor appears when sender assigned to differet signer when conditional rule is already set to one signer, states that reassigning make rule invalid for new signer',
    defaultMessage: 'This field is part of a conditional rule. Reassigning it will also will delete '
      + 'the conditional rule because without it the rule becomes invalid. This cannot be undone.',
  },
  deleteRuleButton: {
    id: '',
    description: 'button text in assigned-to modal in editor appears when sender assigned to differet signer when conditional rule is already set to one signer, when clicked deletes the rule and reassigns',
    defaultMessage: 'Yes, reassign field and delete rule',
  },
  removeFieldHeader: {
    id: '',
    description: 'header text of delete modal in editor appears when trying to delete input field that is part of conditional rule',
    defaultMessage: 'Remove field from rule',
  },
  removeFieldMessage: {
    id: '',
    description: 'message in assigned-to modal in editor, appears when trying to reassing to different user when it is already part of conditional rule, reassigning will remove it from the rule',
    defaultMessage: 'This field is part of a conditional rule. Reassigning it will remove it from '
      + 'the conditional and cannot be undone.',
  },
  removeFieldButton: {
    id: '',
    description: 'button text in assinged-to modal when clicked removes rule from the field and reassigns to different signer',
    defaultMessage: 'Yes, reassign field',
  },
  uploadFailed: {
    id: '',
    description: 'Error message',
    defaultMessage: 'File upload failed',
  },
});

function syncSelectedFieldIds(selectedFieldIds, fields, documents) {
  // Any time either value changes, make sure selectedFieldIds doesn't reference
  // any fields that are gone.
  if (selectedCache.get(fields) !== selectedFieldIds) {
    const existingFieldIds = selectedFieldIds
      .filter(
        (id) => fields.findIndex((f) => f.id === id) >= 0,
      )
      .filter((id) => {
        const field = fields.find((field) => field.id === id);
        const document = findDocumentById(documents, field.documentId);

        return document.editable;
      });
    if (existingFieldIds.length !== selectedFieldIds) {
      selectedCache.set(fields, existingFieldIds);
      return existingFieldIds;
    }

    selectedCache.set(fields, selectedFieldIds);
  }
  return selectedFieldIds;
}

export function normalizeSigners(
  signers,
  signerOrder, // source of truth
  mode,
  senderName = null,
  preparerName = null,
  skipMeNow = false,
) {
  const order = (signerOrder && typeof signerOrder === 'object')
    ? signerOrder
    : {};

  const orders = [-1];

  const sort = (signer, orderNum) => {
    orders.push(orderNum);

    return {
      ...signer,
      order: Number(orderNum),
    };
  };

  // Signers with an order value prepended to list
  const sortPrepend = signers.map((signer) => {
    if (order[signer.id] != null) {
      return sort(signer, signerOrder[signer.id]);
    }

    return { ...signer };
  });

  // Signers without an order value appended to list
  const sortedSigners = sortPrepend.map((signer) => {
    if (order[signer.id] == null) {
      return sort(signer, Math.max(...orders) + 1);
    }

    return { ...signer };
  });

  if (hasFeature(mode, FEATURE_SENDER_ASSIGN)) {
    sortedSigners.push({
      id: SENDER,
      name: senderName,
      order: -1,
    });
  }

  // dont add the PREPARER role if skip me now is set
  if (!skipMeNow) {
    sortedSigners.push({
      id: PREPARER,
      name: preparerName,
      order: -2,
    });
  }

  sortedSigners.sort((a, b) => a.order - b.order);

  // Trim sorted signers email and name if they exists.
  const sortedAndTrimmedSigners = sortedSigners.map((signer) => {

    let { email, name } = signer;
    email = email ? signer.email.trim() : email;
    name = name ? signer.name.trim() : name;

    return {
      ...signer,
      name,
      email,
    };
  });

  return sortedAndTrimmedSigners;
}

class EditorContext extends React.PureComponent {
  static defaultProps = {
    dpi: 80,
    canUseDataValidation: false,
    canUseCustomRegexDataValidation: false,
    apiIdsEnabled: false,
    mergeFields: [],
    embeddedOptions: {},
    isTest: true,
    signers: [],
    suggestionItems: [],
    rules: [],
    shouldRequestSigners: false,
    siteCode: 'S',
    sendEditorMessage: () => {},
  };

  self = React.createRef();

  signatureRequestContextHack = React.createRef();

  constructor(props) {
    super(props);
    this.contextCache = makeContextCacher();

    const {
      accountDateFormat,
      currentRoleId,
      dateFormat,
      documents,
      embeddedOptions,
      fields,
      initialState,
      mergeFields,
      mode,
      pages,
      rules,
      rotationValue,
      signers,
      signerOrder,
      shouldRequestSigners,
      preloadedTsmGroupKey,
      pnsRequestType,
    } = props;

    const normalizedSigners = normalizeSigners(
      signers,
      signerOrder,
      mode,
      this.getSenderName(),
      this.getPreparerName(),
      embeddedOptions.skipMeNow,
    );

    this.fieldOperations = fieldOperations({
      pages,
      signers: normalizedSigners,
      signerOrder,
      mode,
      currentRoleId,
      dateFormat,
      embeddedMergeFields: mergeFields,
      preparer: props.preparer,
      accountDateFormat,
    });

    // Initialize Sender as default on the signer dropdown
    if (hasFeature(this.props.mode, FEATURE_ADVANCED_MERGE_FIELD)
      && mergeFields && mergeFields.length > 0) {
      this.fieldOperations.setDefaultSigner(SENDER);
    }

    let normalizedFields = [...fields];
    normalizedFields.forEach((field, index) => {
      normalizedFields[index] = this.fieldOperations.normalizeField(field, normalizedFields);
    });
    normalizedFields = normalizedFields
      .filter((field) => {
        // remove field if signer is removed
        if (field.signer === -1 || !pages[field.pageIndex]) {
          // console.warn(`Discarding field for page ${field.pageIndex}`);
          return false;
        }

        return true;
      });

    // cleanup rules if available
    const updatedRules = cleanupRules(rules, normalizedFields);

    const mobileEditor = hasFeature(mode, FEATURE_EDITOR_MOBILE);
    this.state = {
      view: 'edit', // 'edit' | 'preview'
      documentReady: document.readyState === 'complete',
      isKeyboardModalOpen: false,
      smallMenu: false,
      selectedFieldIds: [],
      currentPageIndex: 0,
      displayErrorBox: true,
      displaySuccessBox: false,
      bannerMessages: [],
      documents,
      activeUploadIntegration: undefined,
      pages,
      rules: updatedRules,
      fields: this.fieldOperations.sortFields(normalizedFields),
      fakeClipboard: null,
      pageContainer: {},
      newFieldType: mobileEditor ? undefined : 'signature',
      autoFillType: null,
      signers: normalizedSigners,
      preloadedTsmGroupKey,
      signerOrder,
      toolbarStatus: null,
      windowWidth: window.innerWidth,
      openContextMenuIds: [],
      toDelete: null,
      previousFieldSnapshot: {},
      rotationDegree: '0deg',
      rotationValue: rotationValue || 0,
      pnsRequestType,
      replaceSnapshotGuid: null,
      replaceSuccessMessage: '',
      replaceData: null,
      isReplaceDocumentModalOpen: false,
      isRotating: false,
      requestDateFormat: this.props.dateFormat,
      editingRule: null,
      deletingFieldStatus: null,
      fieldsToBeDeleted: [],
      isEditSignersModalOpen: shouldRequestSigners,
      hideContinueButton: this.hideContinueButton,
      showEmbeddedToolbarLayout: this.showEmbeddedToolbarLayout,
      assignedToLeft: 0, // denotes number of clicks of Signer DropDown in Editor
      assignedToRight: 0, // denotes number of clicks of Assigned to DropDown in Editor
    };
    this.state = {
      ...this.state,
      ...this.validateFields(this.state, this.state.fields),
    };

    // Setup a scenario in storybook so I don't have to interact with the
    // storybook every time I make a change.
    if (process.env.NODE_ENV === 'development' && window.location.host === 'localhost:9001') {
      // Normally you shouldn't mutate state, but because this is all being done
      // in the constructor it's ok
      this.state.selectedFieldIds.push('field_3');
      if (initialState) {
        this.state = { ...this.state, ...initialState };
      }
    }

    this.fieldHistory = makeFieldHistory('fields');
    this.wheelEventName = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';
  }


  getIsUploading = () => Boolean(this.state.documents.find(
    (doc) => doc.upload,
  ));

  saveNumberOfClicks = (type) => {
    if (type === ASSIGNED_TO_RIGHT) {
      this.setState((prevState) => ({
        assignedToRight: prevState.assignedToRight + 1,
      }));
    } else if (type === ASSIGNED_TO_LEFT) {
      this.setState((prevState) => ({
        assignedToLeft: prevState.assignedToLeft + 1,
      }));
    }
  };

  static getDerivedStateFromProps(props, state) {
    let nextState = null;
    const selectedFieldIds = syncSelectedFieldIds(
      state.selectedFieldIds,
      state.fields,
      state.documents,
    );
    if (selectedFieldIds !== state.selectedFieldIds) {
      nextState = {
        ...nextState,
        selectedFieldIds,
      };
    }

    const widestPageIndex = props.pages
      .map((p, pageIndex) => ({ ...p, pageIndex }))
      .reduce((a, b) => (
        b.width > a.width ? b : a
      ), { pageIndex: 0, width: 0 })
      .pageIndex;

    if (widestPageIndex !== state.widestPageIndex) {
      nextState = {
        ...nextState,
        widestPageIndex,
      };
    }

    if (!isSorted(state.rules)) {
      nextState = {
        ...nextState,
        rules: sortRules(state.fields, state.rules),
      };
    }

    const isUploading = Boolean(state.documents.find(
      (doc) => doc.upload,
    ));
    if (state.isUploading !== isUploading) {
      nextState = {
        ...nextState,
        isUploading,
      };
    }

    return nextState;
  }

  getSenderName = () => {
    const advancedMergeField = hasFeature(this.props.mode, FEATURE_ADVANCED_MERGE_FIELD);
    const hasMergeFields = this.props.mergeFields?.length > 0;

    if (advancedMergeField && hasMergeFields && this.props.embeddedOptions.integrationMetadata
      && this.props.embeddedOptions.integrationMetadata.context === IntegrationContext.HubSpot) {
      return IntegrationReturnToLabel.HubSpot;
    }

    return intl.formatMessage(messages.nameSender);
  };

  getPreparerName = () => {
    return intl.formatMessage(messages.nameMeNow);
  };

  toggleKeyboardModal = () => {
    if (!this.state.isKeyboardModalOpen) {
      trackHeapCustomEvent('editor_clicked_see_shortcuts');
    }
    this.setState({
      isKeyboardModalOpen: !this.state.isKeyboardModalOpen,
    });
  };

  toggleSmallMenu = () => this.setState(({ smallMenu }) => ({
    smallMenu: !smallMenu,
  }));

  toggleReplaceDocumentModal = () => {
    this.setState({
      isReplaceDocumentModalOpen: !this.state.isReplaceDocumentModalOpen,
    });
  };

  hideContinueButton = () => {
    // The Editor used to always show the continue button,
    // but now, we need to turn it off in some places.
    const { hideContinueButton } = this.props.embeddedOptions;
    return hideContinueButton === true;
  };

  showEmbeddedToolbarLayout = (options = {}) => {
    const { isEmbeddedTemplate = true, isEmbeddedRequest = true } = options;

    return (!this.props.embeddedOptions.hideContinueButton
      && (this.props.embeddedOptions.isEmbeddedTemplate === isEmbeddedTemplate
        || this.props.embeddedOptions.isEmbeddedRequest === isEmbeddedRequest));
  };

  openEditSignersModal = () => {
    this.setState({ isEditSignersModalOpen: true });
  };

  closeEditSignersModal = () => {
    this.setState({ isEditSignersModalOpen: false });
  };

  toggleHyperlinkField = (toggleValue, fieldToConvert) => {

    let field = fieldToConvert;
    if (!field) {
      field = this.getSelectedFields()[0];
    }

    const { pageIndex, x, y, width, height, documentId, value, name } = field;
    this.removeFields([field.id]);

    const newField = {
      signer: PREPARER,
      value,
      name,
      width,
      height,
      documentId,
      pageIndex,
      x,
      y,
    };

    if (toggleValue === true) {
      newField.type = 'hyperlink';
      newField.url = null;
    } else {
      newField.type = 'text';
    }

    // If the field had a default name (Textbox1, Hyperlink1), then drop the
    // name so that it gets regenerated for the new field type.
    if (this.fieldOperations.isDefaultFieldName(field.type, field.name)) {
      delete newField.name;
    }

    this.createNewField(newField);
  };

  toggleMaskedField = (field) => {
    this.updateFields({
      [field.id]: { masked: !field.masked },
    });
  };

  setSignatureRequestDateFormat = (dateFormat) => {
    const updates = this.state.fields
      .filter((field) => field.type === 'date')
      .reduce((allFields, field) => {
        allFields[field.id] = { dateFormat };
        return allFields;
      }, []);

    // set the date format
    this.setState({
      requestDateFormat: dateFormat,
    });

    // update the fields
    return this.updateFields(updates);
  };

  saveRule = (currentRule) => {
    this.setState((prevState) => {
      // get all rules, but remove the current rule if exists,
      // to insert the updated rule if it's being edited
      const rules = prevState.rules
        .filter((rule) => rule.id !== currentRule.id);
      // validate something here?
      rules.push(currentRule);

      return {
        rules,
        editingRule: null,
      };
    });
  };

  deleteRule = (ruleId) => {
    this.setState((prevState) => {
      // remove the rule from the rules array
      const rules = prevState.rules.filter((rule) => rule.id !== ruleId);
      return { rules };
    });
  };

  cancelRuleEdit = () => {
    this.setState({
      editingRule: null,
    });
  };

  createNewRule = () => {
    const editingRule = rulesPanel.buildNewRule(this.getSelectedFields());
    this.setState({
      editingRule,
    });
  };

  updateRule = (ruleId, callback) => {
    this.setState((state) => {
      if (state.editingRule && ruleId === state.editingRule.id) {
        const editingRule = callback(state.editingRule);

        if (editingRule) {
          return { editingRule };
        }
        return null;
      }

      const rules = state.rules.map((rule) => {
        if (rule.id === ruleId) {
          return callback(rule);
        }
        return rule;
      });
      return { rules };
    });

  };

  editRule = (ruleId) => {
    // get the rule to edit
    const editingRule = this.state.rules.find((rule) => rule.id === ruleId);

    this.setState((state) => ({
      editingRule: rulesPanel.editRule(editingRule, state.fields),
    }));
  };

  getRuleIds = (() => { // IIFE to build a local cache
    // I couldn't use lodash.memoize because it seems to only cache based on the
    // first parameter.
    const cache = {
      rules: null,
      editingRule: null,
      ruleActionIds: [],
      ruleTriggerIds: [],
      // Collects a list of triggerIDs that would form a loop if you add them to
      // the current rule.
      ruleActionUnselectableIds: [],
    };

    return () => {
      if (cache.rules !== this.state.rules || cache.editingRule !== this.state.editingRule) {
        cache.ruleActionUnselectableIds = [];

        let rules = this.state.rules;
        if (this.state.editingRule) {
          rules = rules.filter((r) => r.id !== this.state.editingRule);

          if (!hasFeature(this.props.mode, FEATURE_CONDITIONAL_LOGIC_NESTED)) {
            const allFieldIdsFromRules = getFieldIdsFromRules(this.state.fields, rules);
            cache.ruleActionUnselectableIds = allFieldIdsFromRules;
          }

          rules.push(this.state.editingRule);
          rules = sortRules(this.state.fields, rules);

          const currentTriggerIds = this.state.editingRule.triggers.map((t) => t.id);

          const radioGroups = this.state.editingRule.triggers
            .reduce((groups, t) => {
              // if the trigger is a radiobutton, make sure the radio button from
              // the same group isn't selectable as an action
              groups.push(
                ...this.state.fields.filter((f) => {
                  return f.id === t.id && f.type === 'radiobutton';
                }).reduce((groups, f) => {
                  // this is to make typescript happy because it's
                  // complaining about property not existing
                  if (f.type === 'radiobutton') {
                    groups.push(f.group);
                  }
                  return groups;
                }, []),
              );
              return groups;
            }, []);

          if (radioGroups.length > 0) {
            cache.ruleActionUnselectableIds.push(
              ...this.state.fields
                .filter((f) => f.type === 'radiobutton' && radioGroups.includes(f.group))
                .map((f) => f.id),
            );
          }

          // Because the rules are sorted, any rules that might affect
          // editingRule's triggers will be before it in the array. So walk
          // backward from that rule.
          for (let i = rules.indexOf(this.state.editingRule) - 1; i >= 0; i--) {
            const rule = rules[i];

            const isAncestor = rule.actions.some((a) => (
              currentTriggerIds.includes(a.fieldId)
              || cache.ruleActionUnselectableIds.includes(a.fieldId)
            ));

            if (isAncestor) {
              cache.ruleActionUnselectableIds.push(...rule.triggers.map((t) => t.id));
            }
          }
        }

        cache.ruleActionIds = getFieldIdsFromRules(this.state.fields, rules, 'actions');
        cache.ruleTriggerIds = getFieldIdsFromRules(this.state.fields, rules, 'triggers');

        cache.rules = rules;
        cache.editingRule = this.state.editingRule;
      }

      return {
        ruleActionIds: cache.ruleActionIds,
        ruleTriggerIds: cache.ruleTriggerIds,
        ruleActionUnselectableIds: cache.ruleActionUnselectableIds,
      };
    };
  })();

  setPopoverOpen = (id, isOpen) => this.setState((state) => {
    let openContextMenuIds = state.openContextMenuIds;

    if (isOpen) {
      openContextMenuIds = openContextMenuIds.concat([id]);
    } else {
      openContextMenuIds = openContextMenuIds.filter((tmp) => tmp !== id);
    }
    return { openContextMenuIds };
  });

  // Creates a new fields array and rearranges
  // the pageIndexes after deleting a document
  getFieldsToKeep = (documentId) => {
    const { lastRemovedPage, pagesRemovedCount } = this.state.pages
      .reduce((memo, page, pageIndex) => {
        if (page.documentId === documentId) {
          return {
            lastRemovedPage: pageIndex,
            pagesRemovedCount: memo.pagesRemovedCount + 1,
          };
        }
        return memo;
      }, { lastRemovedPage: 0, pagesRemovedCount: 0 });

    return this.state.fields
      .filter((f) => f.documentId !== documentId)
      .map((f) => {
        if (f.pageIndex > lastRemovedPage) {
          return {
            ...f,
            pageIndex: f.pageIndex - pagesRemovedCount,
          };
        }
        return f;
      });
  };

  // This removes a document
  // also remove the pages and fields
  // associated with that document
  onDeleteDocument = (documentId, toDelete) => {
    this.setState((state) => {
      const documents = state.documents.filter(
        (doc) => doc.id !== documentId,
      );

      const pages = state.pages.filter(
        (p) => p.documentId !== documentId,
      );

      const fields = this.getFieldsToKeep(documentId);
      // cleanup rules looking at the remaining fields from above
      const rules = cleanupRules(state.rules, fields);

      const { validationErrors, liveValidationErrors } = this.validateFields(state, fields);

      return { documents, pages, fields, validationErrors, toDelete, rules, liveValidationErrors };
    }, this.autoSave);
  };

  uploadFileFromIntegration = async (integrationType, fileReference, fileName) => {
    const tempId = uuid();
    // Adding temporary document until editor/getData is called again
    const document = {
      id: tempId,
      name: fileName,
      // todo: make editable by handling deletion during in-progress state
      editable: false,
      snapshotGuid: tempId, // temporary
      upload: {
        progress: 0,
      },
    };
    // state.isUploading is computed in getDerivedStateFromProps based on
    // having a document with an `upload` object.
    this.onAddDocument(document);


    const fileConvertInProgressCallback = (polledFile) => {
      this.updateDocument(tempId, (document) => ({
        ...document,
        upload: {
          ...document.upload,
          progress: polledFile.total
            ? polledFile.progress / polledFile.total
            : 0,
        },
      }));
    };

    const isCancelled = () => {
      const foundDocument = Boolean(
        this.state.documents.find((d) => d.id === document.id),
      );
      return foundDocument === false;
    };
    try {
      const { documents, pages, fields } = await this.props.convertFileFromIntegration(
        integrationType,
        fileReference,
        fileName,
        fileConvertInProgressCallback,
        isCancelled,
        this.getReplaceSnapshotGuid(),
      );
      this.clearReplaceSnapshotGuid();
      this.refreshFileData(documents, pages, fields);
    } catch (e) {
      if (isCancelled()) {
        return;
      }
      this.updateDocument(tempId, (document) => ({
        ...document,
        upload: {
          ...document.upload,
          error: intl.formatMessage(messages.uploadFailed),
        },
      }));
      throw new Error(e);
    }

  };

  setReplaceSnapshotGuid = (snapshotGuid) => {
    this.setState({
      replaceSnapshotGuid: snapshotGuid,
    });
  };

  getReplaceSnapshotGuid = () => {
    return this.state.replaceSnapshotGuid;
  };

  clearReplaceSnapshotGuid = async () => {
    this.setState({
      replaceSnapshotGuid: null,
      displaySuccessBox: true,
    });

    // close banner after 90 seconds
    await new Promise((resolve) => setTimeout(resolve, 90000));
    this.setState({ displaySuccessBox: false });
  };

  revertReplace = async () => {
    const { documents, pages, fields } = await this.props.revertReplace();
    this.refreshFileData(documents, pages, fields);
  };

  uploadFile = async (files) => {
    try {
      if (files.length !== 1) {
        throw new Error('There should be exactly one file');
      }
      const [file] = files;
      const { uploadFile, preloadedTsmGroupKey } = this.props;

      const tempId = uuid();
      // Adding temporary document until editor/getData is called again
      const document = {
        id: tempId,
        name: file.name,
        // todo: make editable by handling deletion during in-progress state
        editable: false,
        snapshotGuid: tempId, // temporary
        upload: {
          progress: 0,
        },
      };
      // state.isUploading is computed in getDerivedStateFromProps based on
      // having a document with an `upload` object.
      this.onAddDocument(document);

      // close modal if open
      if (this.state.isReplaceDocumentModalOpen) {
        this.toggleReplaceDocumentModal();
      }

      const initialUploadFile = await uploadFile(
        preloadedTsmGroupKey,
        file,
        this.getReplaceSnapshotGuid(),
      );
      if (!this.state.pnsRequestType) {
        throw new Error('No prep and send request type found');
      } else if (typeof initialUploadFile !== 'string' && 'pwRequired' in initialUploadFile && initialUploadFile.pwRequired) {
        document.pwRequired = true;
        document.rootSnapshotGuid = initialUploadFile.rootSnapshotGuid;
      } else if (typeof initialUploadFile === 'string') {
        throw new Error(`Upload could not take place: ${initialUploadFile}`);
      }

      const convertingFile = {
        name: file.name,
        type: UserFileTypes.Upload,
        rootSnapshotGuid: initialUploadFile.rootSnapshotGuid,
        fields: [],
        order: this.state.documents.length,
        status: FilePollResponseStatus.Converting,
        pwRequired: false,
      };

      const fileConvertInProgressCallback = (polledFile) => {
        this.updateDocument(tempId, (document) => ({
          ...document,
          upload: {
            ...document.upload,
            progress: polledFile.total
              ? polledFile.progress / polledFile.total
              : 0,
          },
        }));
      };

      const isCancelled = () => {
        const foundDocument = Boolean(
          this.state.documents.find((d) => d.id === document.id),
        );
        return foundDocument === false;
      };
      try {

        const { documents, pages, fields } = await this.props.convertUploadedFile(
          initialUploadFile.rootSnapshotGuid,
          convertingFile,
          fileConvertInProgressCallback,
          isCancelled,
        );
        this.refreshFileData(documents, pages, fields);
        // clear replace snapshot guid
        this.clearReplaceSnapshotGuid();
      } catch (e) {
        if (isCancelled()) {
          return;
        }
        this.updateDocument(tempId, (document) => ({
          ...document,
          upload: {
            ...document.upload,
            error: intl.formatMessage(messages.uploadFailed),
          },
        }));
        throw new Error(e);
      }
    } catch (error) {
      throw new Error(error);
    }
    // todo: add support for embedded
  };

  rejectFileUpload = ([file]) => {
    const tempId = uuid();
    const document = {
      id: tempId,
      name: file.name,
      editable: false,
      snapshotGuid: tempId,
      upload: {
        // todo: internationalize
        error: 'File upload failed',
      },
    };
    this.onAddDocument(document);
    // todo: Make it possible to cancel these documents that weren't uploaded to attachment/upload
  };

  onAddDocument = (document) => {
    this.setState((state) => {
      return {
        documents: [...state.documents, document],
      };
    }, this.autoSave);
  };

  cancelDocumentUpload = (documentId) => {
    this.setState((state) => {
      const documents = state.documents.filter(
        (doc) => (documentId !== doc.id),
      );
      return {
        documents,
      };
    }, this.autoSave);
  };

  updateDocument = (id, callback) => {
    this.setState((state) => {
      const documents = state.documents.map((stateDocument) => {
        if (id === stateDocument.id) {
          return callback(stateDocument);
        } else {
          return stateDocument;
        }
      });
      return {
        documents,
      };
    }, this.autoSave);
  };

  refreshFileData = async (documents, pages, fields) => {
    const defaultSigner = this.fieldOperations.signer;
    this.setState({
      documents,
      pages,
      fields,
    });

    const {
      currentRoleId,
      signerOrder,
      signers,
    } = this.state;

    const normalizedSigners = normalizeSigners(
      signers,
      signerOrder,
      this.props.mode,
      this.getSenderName(),
      this.getPreparerName(),
      this.props.embeddedOptions.skipMeNow,
    );

    this.fieldOperations = fieldOperations({
      pages,
      signers: normalizedSigners,
      signerOrder,
      mode: this.props.mode,
      currentRoleId,
      dateFormat: this.props.dateFormat,
      embeddedMergeFields: this.props.mergeFields,
      preparer: this.props.preparer,
      accountDateFormat: this.props.accountDateFormat,
    });
    this.changeDefaultSigner(defaultSigner);
  };

  handlePageThumbnailClick = (pageIndex) => this.setState(() => {

    const page = document.getElementById(`page-${pageIndex}`);
    page.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });

    return { currentPageIndex: pageIndex };
  });

  finishFieldsDeletion = () => {
    this.removeFields(this.state.fieldsToBeDeleted, true);
  };

  closeFieldDeletionModal = () => {
    return this.setState({
      deletingFieldStatus: null,
      fieldsToBeDeleted: [],
    });
  };

  removeFields = (ids, forceDelete = false) => this.setState((state) => {
    // don't delete fields when in conditional logic mode
    // might break stuff
    if (state.editingRule != null) {
      return;
    }

    const fields = this.fieldOperations.deleteFields(state.fields, ids);
    const selectedFieldIds = state.selectedFieldIds.filter(
      (id) => ids.indexOf(id) === -1,
    );

    const rules = cleanupRules(state.rules, fields);
    if (!forceDelete) {
      // If cleanupRules didn't return the original array, then something must have
      // changed.
      if (rules !== state.rules) {
        // Figure out whether a rule was removed, or simply changed
        const deletingFieldStatus = (rules.length !== state.rules.length
          ? 'deleteFieldAndRule'
          // This triggers if a rule targets multiple fields and you're just
          // deleting one of the fields.
          : 'deleteField'
        );
        return { deletingFieldStatus, fieldsToBeDeleted: ids };
      }
    }

    const { validationErrors, liveValidationErrors } = this.validateFields(state, fields);
    return {
      fields,
      selectedFieldIds,
      validationErrors,
      liveValidationErrors,
      rules,
      deletingFieldStatus: null,
      fieldsToBeDeleted: [],
    };
  });

  isValidAddress = (address, additionalFields = []) => {
    // Don't check against selected fields, dragLayer uses `isValidAddress` to
    // preview where something can be moved.
    const fields = this.state.fields.filter(
      (f) => this.state.selectedFieldIds.indexOf(f.id) === -1,
    );

    const document = findDocumentById(this.state.documents, address.documentId);

    if (!document || (document.editable != null && !document.editable)) {
      return false;
    }

    return this.fieldOperations.isValidAddress(fields.concat(additionalFields), address);
  };

  setNewComponentType = (newFieldType, autoFillType = null) => {
    if (newFieldType) {
      trackHeapCustomEvent(`editor_clicked_${newFieldType}`);
    }
    return this.setState({ newFieldType, autoFillType });
  };

  createNewField = (fieldData, selectNewField = true) => {
    this.setState((state) => {
      const type = fieldData.type || state.newFieldType;
      const autoFillType = fieldData.autoFillType || state.autoFillType;
      const defaultSigner = this.fieldOperations.signer;

      const isFieldDisabled = (
        defaultSigner === SENDER && !this.allowSender({ type, autoFillType })
      )
      || (
        defaultSigner === PREPARER && this.hidePreparer({ type, autoFillType })
      );
      if (isFieldDisabled) {
        return null;
      }

      const data = {
        ...this.fieldOperations.getDefaultField(
          type,
          {
            autoFillType,
            isDisabled: isFieldDisabled,
          },
        ),
        ...fieldData,
      };
      if (data.type === 'radiobutton' && !fieldData.group) {
        const generatedGroup = this.generateGroup(state.fields, data.type);
        data.group = generatedGroup.group;
        data.groupLabel = generatedGroup.groupLabel;
        data.requirement = 'require_0-1';
      }

      let documentId = null;
      state.pages.forEach((page, index) => {
        if (data.pageIndex === index) {
          documentId = page.documentId;
        }
      });

      const newField = this.fieldOperations.createNewField(state.fields, {
        type: state.newFieldType,
        documentId,
        ...data,
      });

      const generatedFields = [];
      // If there isn't a group, this was a click-to-create on the page. When
      // you click the + add a button, it supplies the group.
      if (newField.type === 'radiobutton' && !fieldData.group) {
        let y = newField.y + newField.height + RADIO_SPACING;
        // Making sure the second radiobutton gets a new auto-generated field name.
        const combinedFields = [...state.fields, newField];
        let tmp = {
          ...newField,
          id: undefined,
          y,
          name: this.fieldOperations.generateDefaultFieldName(newField, combinedFields),
        };
        tmp = this.fieldOperations.createNewField(state.fields, tmp);

        // createNewField may have moved the field if there wasn't enough room
        // at the bottom of the page.
        if (tmp.y !== y) {
          // Create the 2nd field above the first
          y = newField.y - RADIO_SPACING - newField.height;
          const combinedFields = [...state.fields, newField];
          // Making sure the second radiobutton gets a new auto-generated field name.
          tmp = this.fieldOperations.createNewField(state.fields, {
            ...newField,
            id: undefined,
            y,
            name: this.fieldOperations.generateDefaultFieldName(newField, combinedFields),
          });
        }
        generatedFields.push(tmp);
      }

      trackHeapCustomEvent('enter_data_add_field', { 'Field Type': newField.type });
      generatedFields.forEach(
        (f) => trackHeapCustomEvent('enter_data_add_field', { 'Field Type': f.type }),
      );
      const nextState = {
        selectedFieldIds: selectNewField ? [newField.id] : state.selectedFieldIds,
        fields: this.fieldOperations.sortFields([
          ...state.fields,
          newField,
          ...generatedFields,
        ]),
      };

      return {
        ...nextState,
        ...this.validateFields(nextState, nextState.fields),
      };
    });
  };

  setPreviousFieldSnapshot = (fieldData) => {
    this.setState({
      previousFieldSnapshot: fieldData,
    });
  };

  getPreviousFieldSnapshot = () => {
    let previousFieldData = {};
    const previousFieldSnapshot = this.state.previousFieldSnapshot;
    if (
      typeof previousFieldSnapshot === 'object'
      && Object.keys(previousFieldSnapshot).length > 0
    ) {
      previousFieldData = {
        id: previousFieldSnapshot.id,
        signer: previousFieldSnapshot.signer,
        width: previousFieldSnapshot.width,
        height: previousFieldSnapshot.height,
      };
      // reset previousFieldSnapshot
      this.setState({ previousFieldSnapshot: {} });

      return previousFieldData;
    }
  };

  alignSelected = (direction) => this.setState((state) => {
    const { selectedFieldIds } = state;
    if (selectedFieldIds.length > 1) {
      const fields = this.fieldOperations.alignFields(state.fields, selectedFieldIds, direction);
      this.setState({ fields });
    }
    return null;
  });


  getSelectedFields = makeShallowCache(() => this.state.fields.filter(
    (f) => this.state.selectedFieldIds.indexOf(f.id) >= 0,
  ));

  selectFields = (selectedFieldIds) => {
    if (typeof selectedFieldIds === 'function') {
      const ids = selectedFieldIds(this.state.selectedFieldIds);
      if (ids == null) {
        return;
      }
      return this.selectFields(ids);
    }

    if (!shallowEqual(selectedFieldIds, this.state.selectedFieldIds)) {
      this.setState({ selectedFieldIds });
    }
  };

  isFieldEditable = (fieldId) => {
    const field = this.state.fields.find((field) => field.id === fieldId);
    if (field) {
      const document = findDocumentById(this.state.documents, field.documentId);
      if (document) {
        return document.editable;
      }
    }
    return true;
  };

  selectIntersectingFields = debounce((cornerA, cornerB) => {
    const selectedFieldIds = this.fieldOperations
      .findIntersection(this.state.fields, cornerA, cornerB)
      .map((f) => f.id);

    // This only needs to check the length, when you begin dragging it will
    // intersect 0 fields and will reset  the selection. I guess this could
    // break if you can manage to  de-select 1 field and select a new field at
    // exactly the same time. I'm not sure how to achieve that at the moment.
    if (!shallowEqual(selectedFieldIds, this.state.selectedFieldIds)) {
      this.selectFields(selectedFieldIds);
    }
  });

  generateGroup = (fields, type) => {
    return {
      group: Math.random().toString(16).slice(2),
      requirement: 'require_1',
      groupLabel: this.fieldOperations.generateDefaultGroupFieldName(type, fields),
    };
  };

  updateFields = (updatedFields) => {
    // This is really useful for figuring out why something is changing in the
    // Editor. Every change must pass through this point, but the trace doesn't
    // work inside `setState`.
    // console.trace('updatedFields', updatedFields);
    return this.setState((state) => {
      const fields = this.fieldOperations.mergeFields(state.fields, updatedFields);
      let selectedFieldIds = state.selectedFieldIds;

      // If this operation is creating new fields...
      if (state.fields.length !== fields.length) {
        // Then select the new fields
        selectedFieldIds = this.fieldOperations.findChanged(state.fields, fields)
          .map((f) => f.id);
      }

      const { validationErrors, liveValidationErrors } = this.validateFields(state, fields);

      return { fields, selectedFieldIds, validationErrors, liveValidationErrors };
    });
  };

  assignSelectedFields = (signer, confirmRuleChange) => {
    const { rules } = this.state;
    const selectedFields = this.getSelectedFields();
    trackHeapCustomEvent('enter_data_assign_signer');

    // Rules can't cross signers, so I'm going to run cleanupRules as if these
    // rules  were deleted to see if changing the signers would require any
    // rules to change.
    const ids = this.expandFieldListToIncludeGroups(selectedFields);
    const fields = this.state.fields.filter((f) => !ids.includes(f.id));
    const updatedRules = cleanupRules(this.state.rules, fields);


    // If the user has already confirmed through the modal, then make the changes
    if (confirmRuleChange) {
      // Walk through all the rules
      for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];

        const updated = updatedRules.find((r) => r.id === rule.id);
        if (updated == null) {
          // If cleanupRules deleted the rule, delete the rule
          this.deleteRule(rule.id);
        } else if (updated !== rule) {
          // If cleanupRules changed the rule, replace it
          this.updateRule(
            rule.id,
            // @ts-ignore See the comment in editor-context.d.ts
            () => updated,
          );
        }
      }
    } else if (
      // If cleanupRules didn't return the original array, then something must
      // have changed.
      updatedRules !== rules
    ) {
      if (updatedRules.length !== rules.length) {
        // delete rule
        this.setState({
          confirmAssignSelectedFields: {
            header: intl.formatMessage(messages.deleteRuleHeader),
            message: intl.formatMessage(messages.deleteRuleMessage),
            button: intl.formatMessage(messages.deleteRuleButton),
            callback: () => this.assignSelectedFields(signer, true),
          },
        });
        return;
      } else {
        // remove field from rule
        this.setState({
          confirmAssignSelectedFields: {
            header: intl.formatMessage(messages.removeFieldHeader),
            message: intl.formatMessage(messages.removeFieldMessage),
            button: intl.formatMessage(messages.removeFieldButton),
            callback: () => this.assignSelectedFields(signer, true),
          },
        });
        return;
      }
    }

    const updates = ids.reduce((updates, id) => {
      updates[id] = { signer };
      return updates;
    }, {});
    this.updateFields(updates);
  };

  // If any of the fields are in a group, buildSignerUpdates will expand the
  // update to the whole group.
  expandFieldListToIncludeGroups = (selectedFields) => {
    let ids = selectedFields.map((f) => f.id);
    const allFields = this.state.fields;

    const hasGroup = (fieldData) => (
      'group' in fieldData
        && fieldData.group != null
    );
    if (selectedFields.some(hasGroup)) {
      const groups = selectedFields.filter(hasGroup).map((f) => f.group);

      const fieldIdsFromGroup = allFields.filter(
        (fieldData) => hasGroup(fieldData) && groups.includes(fieldData.group),
      ).map(
        (fieldData) => fieldData.id,
      );

      // This will contain duplicate IDs, but they'll get combined as part of
      // the reducer below.
      ids = [...ids, ...fieldIdsFromGroup];
    }

    return ids;
  };

  validateFields = (state, fields, forceValidation = false) => {
    let validationErrors = state.validationErrors;

    let liveValidationErrors = this.fieldOperations.validateFields(fields, this.props.siteCode);

    if (state.liveValidationErrors && liveValidationErrors) {
      // This will end up going into context, so it's important to make sure it
      // doesn't change if there is no meaningful difference. It'll either be
      // null, or an array of objects about the error. Every time
      // `this.fieldOperations.validateFields` runs it has to return a new array
      // and new error objects. (NOTE: These are not new Error objects)
      if (JSON.stringify(state.liveValidationErrors) === JSON.stringify(liveValidationErrors)) {
        liveValidationErrors = state.liveValidationErrors;
      }
    }

    // Validation errors don't show up until you try to continue, and then this
    // will keep them in sync so you don't have to click continue to see the
    // next error to resolve.
    if (validationErrors || forceValidation) {
      validationErrors = liveValidationErrors;
    }
    return { validationErrors, liveValidationErrors };
  };

  // fieldData is the field that called this
  insertSignature = (signature, fieldData, insertAll) => {

    if (insertAll) {
      const targetFields = this.state.fields.filter((f) => (
        f.type === fieldData.type
        && f.signer === fieldData.signer
      ));
      // Set the key for all fields to be the id of the field
      this.updateFields(targetFields.reduce((acc, field) => {
        acc[field.id] = { signature };
        return acc;
      }, {}));
    } else {
      this.updateFields({
        [fieldData.id]: { signature },
      });
    }
  };

  nudge = (e, direction, resize) => {
    e.preventDefault();

    this.setState((state) => {
      const isSelected = (f) => state.selectedFieldIds.indexOf(f.id) >= 0;
      const updates = state.fields
        .filter(isSelected)
        .reduce((updates, fieldData) => {
          updates[fieldData.id] = nudge(fieldData, direction, resize);
          return updates;
        }, {});

      const fields = this.fieldOperations.mergeFields(this.state.fields, updates);

      // Verify that all of the fields landed in valid locations before saving.
      const allValid = fields.filter(isSelected).reduce(
        (valid, fieldData) => valid && (
          this.fieldOperations.isValidAddress(fields, fieldData)
        ),
        true,
      );
      if (!allValid) {
        return null;
      }

      return { fields };
    });
  };

  getCenter = () => {
    return {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
      center: true,
    };
  };

  // When editing a text field we should use the native shortcuts. ctrl+a should
  // select all the text, not select all the fields.
  areShortcutsDisabled = () => (
    // If we don't have the context yet, then it's definitley too early to
    // enable keyboard shortcuts. This is an edge case around triggering the
    // keyboard handler while refreshing/loading the page.
    // disable if the rules panel is showing
    this.signatureRequestContextHack.current == null
    || this.signatureRequestContextHack.current.areShortcutsDisabled()
    || this.state.editingRule != null
  );

  selectAll = (e) => {
    e.preventDefault();
    this.setState((state) => ({
      selectedFieldIds: state.fields.map((f) => f.id),
    }));
  };

  nextOrPreviousSigner = (e, direction) => {
    e.preventDefault();

    const updates = this.getSelectedFields().reduce((updates, fieldData) => {
      let signers = this.state.signers;
      switch (fieldData.type) {
        case 'date':
        case 'signature':
        case 'initials': {
          // These field types can't be merge fields
          const filterCondition = this.props.embeddedOptions.isEmbedded
            ? (s) => s.id !== SENDER && s.id !== PREPARER
            : (s) => s.id !== SENDER;
          signers = signers.filter(filterCondition);
          break;
        }
        default:
      }

      const index = signers.findIndex((s) => String(s.id) === String(fieldData.signer));
      // This will loop around either direction, and even lands on a valid
      // signer is invalid and index is -1
      const next = (signers.length + index + direction) % signers.length;

      updates[fieldData.id] = {
        signer: signers[next].id,
      };
      return updates;
    }, {});

    return this.updateFields(updates);
  };

  undo = () => {
    trackHeapCustomEvent('editor_clicked_undo');
    this.setState(this.fieldHistory.undo);
  };

  redo = () => {
    trackHeapCustomEvent('editor_clicked_redo');
    this.setState(this.fieldHistory.redo);
  };

  handleKeyDown = (e) => {

    if (this.areShortcutsDisabled()) {
      return;
    }

    // IE: Ctrl +/- are "Unidentified", so it needs to fall back on the keyCode
    let key = e.key !== 'Unidentified' ? e.key : undefined;
    // Safari doesn't support .key, so we have to fall back to .keyCode
    key = key || e.keyCode;

    // metaKey is the Mac's CMD key
    if ((IS_MAC && e.metaKey) || e.ctrlKey) {
      switch (key) {
        case 65:
        case 'a': return this.selectAll(e);
        case 90:
        case 'z':
          e.preventDefault();
          return this.undo();
        case 89:
        case 'y':
          e.preventDefault();
          return this.redo();
        case 68:
        case 'd': return this.duplicate(e);

        case 74:
        case 'j':
          return this.nextOrPreviousSigner(e, 1);
        case 75:
        case 'k':
          return this.nextOrPreviousSigner(e, -1);

        case 38: // Safari
        case 'Up': // IE
        case 'ArrowUp':
          if (window !== window.top && process.env.IS_STORYBOOK) {
            window.top.location = window.location;
          }
          break;
        default:
          break;
      }
    } else {
      switch (key) {
        case 191:
        case '?': return this.setState({ isKeyboardModalOpen: true });
        case 46: // Safari
        case 'Del': // IE
        case 'Backspace':
        case 'Delete':
          e.preventDefault();
          return this.removeFields(
            this.state.selectedFieldIds,
          );
        case 37: // Safari
        case 'Left': // IE
        case 'ArrowLeft':
          return this.nudge(e, 'W', e.shiftKey);
        case 38: // Safari
        case 'Up': // IE
        case 'ArrowUp':
          return this.nudge(e, 'N', e.shiftKey);
        case 39: // Safari
        case 'Right': // IE
        case 'ArrowRight':
          return this.nudge(e, 'E', e.shiftKey);
        case 40: // Safari
        case 'Down': // IE
        case 'ArrowDown':
          return this.nudge(e, 'S', e.shiftKey);

        default:
          // console.log(e.key, e.which);
          break;
      }
    }

    // Adding shortcuts for IE11
    if (navigator.userAgent.match(/Trident\/7\./) && e.ctrlKey) {
      switch (key) {
        case 67:
        case 'c': return this.handleCopy(e);
        case 86:
        case 'v': return this.handlePaste(e);
        default:
          break;
      }
    }
  };


  duplicate = (e) => {
    const selected = this.getSelectedFields();
    if (selected.length > 0) {
      e.preventDefault();
      const newFields = selected.map(
        // Remove the IDs
        ({ id, ...fieldData }) => fieldData,
      );

      const fields = this.fieldOperations.mergeFields(this.state.fields, newFields);

      const selectedFieldIds = this.fieldOperations.findChanged(this.state.fields, fields)
        .map((f) => f.id);

      this.setState({ fields, selectedFieldIds });
    }
  };

  handleCut = (e) => {
    if (this.areShortcutsDisabled()) {
      return;
    }
    if (this.state.selectedFieldIds.length > 0) {
      this.handleCopy(e);
      this.removeFields(this.state.selectedFieldIds);
    }
  };

  getCopyType = () => {
    // For IE 11
    if (navigator.userAgent.match(/Trident\/7\./)) {
      return COPY_TYPE_IE;
    }
    return COPY_TYPE;
  };

  /**
   * When user selects an individual control from a group they can't copy and paste
   * just that button & value; the whole group is copied and pasted.
   */
  getSelectedFieldsForCopy = () => {
    const selected = this.getSelectedFields();

    const selectedGroupFields = selected.filter((field) => !!field.group);

    // bucket selected grouped controls by group id
    const controlsByGroup = selectedGroupFields.reduce((acc, field) => {
      acc[field.group] = (acc[field.group] || []).concat(field);
      return acc;
    }, {});
    // select additional controls per group if necessary
    const updatedGroupFields = Object.keys(controlsByGroup).map((groupId) => {
      if (controlsByGroup[groupId].length > 1) {
        return controlsByGroup[groupId];
      }
      return this.state.fields.filter(
        (field) => field.group === groupId,
      );
    }).flat();

    return [
      ...selected.filter((field) => !field.group),
      ...Object.values(updatedGroupFields),
    ];
  };

  handleCopy = (e) => {
    if (this.areShortcutsDisabled()) {
      return;
    }
    e.preventDefault();
    const selected = this.getSelectedFieldsForCopy();

    trackHeapCustomEvent('editor_clicked_copy');
    if (selected.length > 0) {
      const fields = selected.map(cleanField);
      const fakeClipboard = JSON.stringify(fields, null, 2);

      // I could use a custom data type like `hs/fields`, but by using
      // `text/plain` you could paste it into another file to look at it, or even
      // paste fields from a file back into the editor.
      (e.clipboardData || window.clipboardData).setData(this.getCopyType(), fakeClipboard);
      this.setState({ fakeClipboard });
    }
  };

  handlePaste = (e) => {
    if (this.areShortcutsDisabled()) {
      return;
    }
    // https://developer.mozilla.org/en-US/docs/Web/Events/paste
    const data = (e.clipboardData || window.clipboardData).getData(this.getCopyType())
      || this.state.fakeClipboard;
    if (!data) {
      return;
    }

    if (this.pasteFields(data)) {
      // Prevent the default pasting event and stop bubbling
      e.preventDefault();
      e.stopPropagation();
    }
  };

  paste = async (data) => {
    if (
      data == null
      && navigator.permissions
      && navigator.clipboard
    ) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'clipboard-read' });
        if (permissionStatus.state === 'granted') {
          const text = await navigator.clipboard.readText();
          return this.pasteFields(text);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error.message);
      }
    }

    return this.pasteFields(this.state.fakeClipboard);
  };

  pasteFields = (data) => {
    let pastedFields;
    try {
      pastedFields = JSON.parse(data);
    } catch (e) {
      // Ignore parse errors
    }
    trackHeapCustomEvent('editor_clicked_paste');
    if (pastedFields && pastedFields.length > 0) {
      const pageIndex = this.signatureRequestContextHack.current.yToPageIndex(this.getCenter().y);
      const { width: maxX, height: maxY, documentId } = this.props.pages[pageIndex];

      const {
        left, top, right, bottom,
      } = pastedFields.reduce((memo, f) => ({
        top: Math.min(memo.top, f.y),
        left: Math.min(memo.left, f.x),
        right: Math.max(memo.right, f.x + f.width),
        bottom: Math.max(memo.bottom, f.y + f.height),
      }), {
        left: Infinity, top: Infinity, right: 0, bottom: 0,
      });

      const leftMargin = (maxX - (right - left)) / 2;
      const topMargin = (maxY - (bottom - top)) / 2;
      const fields = this.state.fields;
      const addedAutoGeneratedFields = [];
      const newGroupMap = {};
      const autoGeneratedGroupLabelMap = {};
      const editedGroupFields = [];
      const cleanedFields = pastedFields.map(cleanField).map(
        (f) => {
          if ('name' in f
          && f.name != null) {
            const combinedFields = [...fields, ...addedAutoGeneratedFields];
            f.name = this.fieldOperations.generateDefaultFieldName(f, combinedFields);
            addedAutoGeneratedFields.push(f);
          }

          if (f.group) {
            // Handle creating new Group Labels
            if (autoGeneratedGroupLabelMap[f.group]) {
              f.groupLabel = autoGeneratedGroupLabelMap[f.group];
            } else {
              // create new group name
              const newFields = [...fields, ...editedGroupFields];
              f.groupLabel = this.fieldOperations.generateDefaultGroupFieldName(f.type, newFields);
              editedGroupFields.push(f);
              autoGeneratedGroupLabelMap[f.group] = f.groupLabel;
            }

            // Handle creating new groups
            if (newGroupMap[f.group]) {
              f.group = newGroupMap[f.group];
            } else {
            // create new group
              const newGroup = Math.random().toString(16).slice(2);
              newGroupMap[f.group] = newGroup;
              f.group = newGroup;
            }
          }

          return {
            ...f,
            documentId,
            pageIndex,
            // Paste everything centered on the page
            x: f.x - left + leftMargin,
            y: f.y - top + topMargin,
          };
        },
      );


      this.setState((state) => {
        const fields = this.fieldOperations
          .mergeFields(state.fields, cleanedFields);

        const selectedFieldIds = this.fieldOperations.findChanged(state.fields, fields)
          .map((f) => f.id);

        return { fields, selectedFieldIds };
      });
      return true;
    }
    return false;
  };

  save = (autoSave = false) => {
    const toDelete = this.state.toDelete;

    if (toDelete) {
      // Always allow the save through when it's deleting a document.
      this.setState({ toDelete: null });
    } else if (this.state.isUploading) {
      return;
    }
    return this.props.onSave({
      documents: this.state.documents,
      pages: this.state.pages,
      fields: this.state.fields,
      rules: this.state.rules,
      signers: this.state.signers,
      signerOrder: this.state.signerOrder,
      toDelete,
      rotate: this.state.rotationValue,
      dateFormat: this.state.requestDateFormat,
      assignedToLeft: this.state.assignedToLeft,
      assignedToRight: this.state.assignedToRight,
    }, { autoSave });
  };

  autoSave = async () => {
    this.setState({ toolbarStatus: 'Saving' });
    await this.save(true);

    this.setState({ toolbarStatus: 'Saved' });
    await new Promise((resolve) => setTimeout(resolve, 3000));
    this.setState({ toolbarStatus: null });
  };

  onCancel = () => {
    if (this.state.isUploading) {
      return;
    }
    const cancelWarningMsg = intl.formatMessage(messages.cancelWarningMsg);

    // eslint-disable-next-line no-alert, no-restricted-globals
    if (confirm(cancelWarningMsg)) {
      this.props.onCancel();
    }
  };

  onContinue = async () => {
    if (this.state.isUploading) {
      return;
    }
    await this.save(true);

    this.selectFields([]);
    // This function won't get called until after the state has been updated and
    // this component has been re-rendered. That allows the currently selected
    // component to get de-activated and commit any pending changes before
    // validation runs.
    //
    // To verify:
    // 1. Create a new text field
    // 2. Set it to "Me (when sending)"
    // 3. Without giving it a label, click continue.
    // 4. With this fix in place, you will see an error about it missing a
    //    label. Without this fix, the validation was running against a copy of
    //    the field where the signer had not been changed yet.
    this.setState(() => {
      const { validationErrors, liveValidationErrors } = this.validateFields(
        this.state,
        this.state.fields,
        true,
      );

      if (validationErrors == null) {
        this.clearAutoSaveTimer();
        this.props.onContinue();
      }
      return { validationErrors, liveValidationErrors, displayErrorBox: true };
    });
  };

  hideErrorBox = () => this.setState({ displayErrorBox: false });

  hideSuccessBox = () => this.setState({ displaySuccessBox: false });

  componentDidUpdate(prevProps, prevState) {
    this.fieldHistory.didUpdate(prevState, this.state);

    if (prevState.documentReady !== this.state.documentReady && typeof this.props.onReady === 'function') {
      this.props.onReady();
    }

    // If we have validation errors
    if (
      this.state.validationErrors
      && this.state.validationErrors.length > 0
      // on a field
      && this.state.validationErrors[0].id != null
      // Only trigger when the validation errors changed. I need to stringify
      // them because each time validation runs it generates a new array and new
      // objects.
      && JSON.stringify(this.state.validationErrors) !== JSON.stringify(prevState.validationErrors)
    ) {
      const prevId = (
        prevState.validationErrors
        && prevState.validationErrors.length > 0
        && prevState.validationErrors[0].id
      );
      const { id } = this.state.validationErrors[0];

      if (
        // and it's for a different field
        id !== prevId
        // or the erroring field isn't selected
        || this.state.selectedFieldIds.join(',') !== id
      ) {
        // select it so that the popover shows and the user can correct the
        // error
        this.selectFields([id]);
      }
    }

    if (
      this.props.autoSave > 0
      // autoSave if the fields or rules changed
      && (
        this.state.fields !== prevState.fields
        || this.state.rules !== prevState.rules
        || this.state.signers !== prevState.signers
      )
    ) {
      this.clearAutoSaveTimer(true);
    }
  }

  handleResize = debounce((e) => {
    // If a width wasn't specified, force a re-render with a context update
    if (!this.props.width) {
      this.setState({ windowWidth: e.target.innerWidth });
    }
  });

  /**
   * This clears the auto save timer if it exists. This is important
   * for embedded requests since, if the request is force saved
   * before the auto save is triggered, the server responds
   * with a 400 error on the auto save since the force
   * save completes the request rather than a draft
   *
   * @param  {Boolean} shouldSet
   * @return {void}
   */
  clearAutoSaveTimer(shouldSet = false) {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
    if (shouldSet) {
      this.autoSaveTimer = setTimeout(this.autoSave, this.props.autoSave);
    }
  }

  componentDidMount() {
    if (!this.state.documentReady) {
      this.readyInterval = setInterval(this.checkReady, 100);
      this.checkReady();
    } else if (typeof this.props.onReady === 'function') {
      this.props.onReady();
    }

    trackHeapCustomEvent('editor_place_fields_view');

    // initial rotation here
    if (this.state.rotationValue && this.state.rotationValue > 0) {
      this.rotatePages(true);
    }

    const addListeners = (win) => {
      win.addEventListener('keydown', this.handleKeyDown);
      win.addEventListener('cut', this.handleCut);
      win.addEventListener('copy', this.handleCopy);
      win.addEventListener('paste', this.handlePaste);
      win.addEventListener('resize', this.handleResize);
    };
    addListeners(window);
  }

  componentWillUnmount() {
    if (this.readyInterval) {
      clearInterval(this.readyInterval);
    }

    this.handleResize.cancel();
    this.selectIntersectingFields.cancel();

    try {
      const removeListeners = (win) => {
        win.removeEventListener('keydown', this.handleKeyDown);
        win.removeEventListener('cut', this.handleCut);
        win.removeEventListener('copy', this.handleCopy);
        win.removeEventListener('paste', this.handlePaste);
        win.removeEventListener('resize', this.handleResize);
      };
      removeListeners(window);
      if (window.parent) {
        removeListeners(window.parent);
      }
    } catch (e) {
      // cannot attach event listener to parent for embedded
    }
  }

  checkReady = () => {
    const documentReady = document.readyState === 'complete';
    if (documentReady) {
      clearInterval(this.readyInterval);
      this.readyInterval = null;

      this.setState({ documentReady });
    }
  };

  changeSigners = ({ signers, signerOrder }) => {
    const normalized = normalizeSigners(
      signers,
      signerOrder,
      this.props.mode,
      this.getSenderName(),
      this.getPreparerName(),
      this.props.embeddedOptions.skipMeNow,
    );

    this.fieldOperations.setSigners(normalized);

    // If signer removed that was default signer, update to first non-hidden signer
    let defaultSigner = normalized.find((signer) => {
      return parseInt(signer.id, 10) === parseInt(this.fieldOperations.signer, 10);
    });

    // If default signer doesn't exit let's try to find first non-preparer first
    if (!defaultSigner) {
      defaultSigner = normalized.find((signer) => {
        return (signer.hidden === undefined || signer.hidden === false) && signer.id !== PREPARER;
      });

      this.changeDefaultSigner(defaultSigner.id, false);
    }

    // We didn't find any non-preparer let's include preparer in the search as well
    if (!defaultSigner) {
      defaultSigner = normalized.find((signer) => {
        return signer.hidden === undefined || signer.hidden === false;
      });

      this.changeDefaultSigner(defaultSigner.id, false);
    }

    // Remove any fields belonging to removed signers
    const signerIds = normalized.map((signer) => String(signer.id));
    const fields = this.state.fields.filter((f) => {
      return signerIds.indexOf(String(f.signer)) !== -1;
    });

    const { validationErrors, liveValidationErrors } = this.validateFields(this.state, fields);
    // cleanup rules after deleting a signer
    const rules = cleanupRules(this.state.rules, fields);

    this.setState({
      signers: normalized,
      signerOrder,
      fields,
      selectedFieldIds: [],
      validationErrors,
      liveValidationErrors,
      rules,
    });
  };

  changeDefaultSigner = (signerId, shouldUpdate = true) => {
    this.fieldOperations.setDefaultSigner(signerId);
    if (shouldUpdate) {
      this.forceUpdate();
    }
  };

  /**
   * Embedded editor must choose "$AppName (when sending)" by default
   */
  embeddedDefaultSigner = () => {
    const isEmbedded = this.props.embeddedOptions.isEmbeddedTemplate
        && this.props.embeddedOptions.appName;

    if (!isEmbedded) {
      return;
    }

    this.fieldOperations.setEnforcedDefaultSigner(SENDER);
  };

  /**
  * Rotate pages if allowed
  * boolean isInitial if initial rotation specified, bypass certain checks
  */
  rotatePages = (isInitial = false) => {
    // check if feature available here
    if (!isInitial && !hasFeature(this.props.mode, FEATURE_ROTATE_PAGES)) {
      return;
    }

    // if any fields on the doc, don't allow rotation
    if (!isInitial && this.state.fields.length > 0) {
      return;
    }

    // if user clicks it multiple times, don't rotate until state changes
    if (this.state.isRotating) {
      return;
    }

    // rotate between these values
    const defaultRotations = ['0deg', '90deg', '180deg', '-90deg'];
    // 1 & 3 means, the orientation is different from original
    // corresponds to '90deg' & '-90deg' respectively
    const changedOrientationValues = [1, 3];

    return this.setState((state) => {
      // if initial, use the provided rotation value
      const nextRotationValue = isInitial ? state.rotationValue : (state.rotationValue + 1) % 4;
      const pages = state.pages.map((page) => {
        // default values for rotating
        const defaultDimensions = {
          width: page.height,
          height: page.width,
          newOrientation:
            // flipping the orientation between 1 & 0
            Number(!(page.newOrientation != null ? page.newOrientation : page.orientation)),
        };
        // this handles initial page rotation, when read from the saved rotate value
        // in the overlay daya
        if (isInitial) {
          const shouldChange = changedOrientationValues.includes(nextRotationValue);
          // set original width & height, since the correct dimensions are passed in
          // if there's an initial page rotation available in the backend
          defaultDimensions.width = page.width;
          defaultDimensions.height = page.height;
          // pick correct rotation value, so appropriate styles are used
          defaultDimensions.newOrientation = shouldChange
            ? Number(!page.orientation)
            : Number(page.orientation);
        }

        return {
          ...page,
          ...defaultDimensions,
        };
      });

      return {
        pages,
        rotationValue: nextRotationValue,
        rotationDegree: defaultRotations[nextRotationValue],
        isRotating: true,
      };

    }, async () => {
      // autosave this and update the pages so it works with the fields
      await this.autoSave();
      this.fieldOperations.setPages(this.state.pages);
      // complete rotation
      this.setState({ isRotating: false });
    });
  };

  addBannerMessage = (translatedText, id = uuid()) => {
    this.setState((state) => ({
      bannerMessages: state.bannerMessages
        .filter((n) => n.id !== id)
        .concat({
          id,
          text: translatedText,
          // The notification banner is only styled to display errors.
          type: 'error',
        }),
    }));

    return id;
  };

  removeBannerMessage = (id) => {
    this.setState((state) => ({
      bannerMessages: state.bannerMessages.filter((n) => n.id !== id),
    }));
  };

  reorderDocuments = async (snapshotGuid, from, to) => {
    return new Promise((resolve) => {
      const orderedSnapshotGuids = [...this.state.documents];
      const element = orderedSnapshotGuids[from];
      orderedSnapshotGuids.splice(from, 1);
      orderedSnapshotGuids.splice(to, 0, element);
      this.setState({
        documents: orderedSnapshotGuids,
      }, async () => {
        this.autoSave();
        resolve();
      });
    });
  };

  assertRulesAreValid = (rules, fields) => {
    const isValid = validateRules(rules, fields);
    if (isValid === true) {
      return true;
    }

    // If the rules are invalid, there is nothing the user can do to fix them.
    // Other parts of the UI are supposed to prevent creating invalid rules.
    throw new Error(`Validation error: ${JSON.stringify(isValid)}`);
  };

  setView = (view) => this.setState({ view });

  // allowSender and hidePreparer
  // Had to add this two functions here as they are checking if field is disabled for selected
  // signer or not. But because we can create fields by double clicking, need to check if
  // field is disabled or not stays, and for code cleanness added this function here and referenced
  // them from field container
  // This is exactly same function as in hellospa/components/editor/settings-panel/assigned-to.tsx
  // Differences are:
  // * This function does not check for grouped checkboxes, this functionality
  //    was moved onto checkbox settings
  // * Signature is different
   allowSender = (f = { type: '', autoFillType: null }) => {
     const { mode, mergeFields } = this.props;

     return !(
       // Not a valid type for sender
       (f.type !== 'text' && f.type !== 'checkbox')

       // Hiding this for autofill fields
       || (f.autoFillType)

       // Or if we're in embed mode and there are no merge fields for this type
       || (
         hasFeature(mode, FEATURE_EMBED)
         && !mergeFields.some(({ type }) => type === f.type)
       )
       // Or if we're in "Use Template" with one-off uploaded file
       || (
         hasFeature(mode, FEATURE_TEMPLATE_ONE_OFF_FILE)
       )
     );
   };

  // This is exactly same function as in hellospa/components/editor/settings-panel/assigned-to.tsx
  // * Signature is different
  hidePreparer = (f = { type: '', autoFillType: null }) => {
    const { mode } = this.props;

    // if a field is dropdown, hide the PREPARER role
    if (f.type === 'dropdown' || f.type === 'radiobutton') {
      return true;
    }

    // if the field is a signature or initials field and
    // it's in an embedded context, hide the PREPARER
    if (
      (f.type === 'signature' || f.type === 'initials')
      && (hasFeature(mode, FEATURE_EMBED) && hasFeature(mode, FEATURE_TEMPLATE_ROLES))
    ) {
      return true;
    }

    return false;
  };

  setActiveUploadIntegration = (activeUploadIntegration) => {
    this.setState({ activeUploadIntegration });
  };

  render() {
    const { mode } = this.props;
    const { documents, pages, fields, documentReady, isUploading } = this.state;

    if (!documentReady) {
      // This prevents rendering before the CSS has loaded.
      return <span data-document-ready="false" />;
    }

    this.assertRulesAreValid(this.state.rules, this.state.fields);

    const { ruleActionIds, ruleTriggerIds, ruleActionUnselectableIds } = this.getRuleIds();
    const context = this.contextCache({
      // data
      ...{
        apiIdsEnabled: this.props.apiIdsEnabled,
        canUseDataValidation: this.props.canUseDataValidation,
        canUseCustomRegexDataValidation: this.props.canUseCustomRegexDataValidation,
        dateFormat: this.props.dateFormat,
        accountDateFormat: this.props.accountDateFormat,
        fields,
        hasOpenContextMenus: this.state.openContextMenuIds.length > 0,
        isKeyboardModalOpen: this.state.isKeyboardModalOpen,
        currentPageIndex: this.state.currentPageIndex,
        pnsRequestType: this.state.pnsRequestType,
        preloadedTsmGroupKey: this.state.preloadedTsmGroupKey,
        replaceSnapshotGuid: this.state.replaceSnapshotGuid,
        isReplaceDocumentModalOpen: this.state.isReplaceDocumentModalOpen,
        isTest: this.props.isTest,
        mode,
        newFieldType: this.state.newFieldType,
        autoFillType: this.state.autoFillType,
        isUploading,
        documents,
        pages,
        selectedFieldIds: this.state.selectedFieldIds,
        currentRoleId: this.props.currentRoleId,
        signerId: this.props.signerId,
        signers: this.state.signers,
        signerOrder: this.state.signerOrder,
        defaultSigner: this.fieldOperations.signer,
        smallMenu: this.state.smallMenu,
        toolbarStatus: this.state.toolbarStatus,
        // Before the new P&S validation would only run when you press Continue.
        // As long as there are validation errors, they update on every render
        // and fields with errors are automatically selected. Any
        // components/sidebars that are aware of validation errors currently use
        // this.
        validationErrors: this.state.validationErrors,
        // liveValidationErrors gets updated on every render and is NOT
        // connected to any fields/sidebars. It's used by RemoteEditorValidation
        // to send validation info to P&S.
        liveValidationErrors: this.state.liveValidationErrors,
        bannerMessages: this.state.bannerMessages,
        mainBtnText: this.props.mainBtnText,
        mergeFields: this.props.mergeFields,
        embeddedOptions: this.props.embeddedOptions,
        suggestionItems: this.props.suggestionItems,
        setActiveUploadIntegration: this.setActiveUploadIntegration,
        activeUploadIntegration: this.state.activeUploadIntegration,
        uploadIntegrations: this.props.uploadIntegrations,
        rotationDegree: this.state.rotationDegree,
        rotationValue: this.state.rotationValue,
        isRotating: this.state.isRotating,
        displayErrorBox: this.state.displayErrorBox,
        displaySuccessBox: this.state.displaySuccessBox,
        showRulesPanel: this.state.editingRule != null,
        rules: this.state.rules,
        currentRule: this.state.currentRule,
        editingRule: this.state.editingRule,
        ruleActionIds,
        ruleTriggerIds,
        ruleActionUnselectableIds,
        deletingFieldStatus: this.state.deletingFieldStatus,
        isEditSignersModalOpen: this.state.isEditSignersModalOpen,
        shouldRequestSigners: this.props.shouldRequestSigners,
        hideContinueButton: this.hideContinueButton,
        showEmbeddedToolbarLayout: this.showEmbeddedToolbarLayout,
        siteCode: this.props.siteCode,
        view: this.state.view,
        preparer: this.props.preparer,
      },
      // functions
      addBannerMessage: this.addBannerMessage,
      removeBannerMessage: this.removeBannerMessage,
      alignSelected: this.alignSelected,
      createNewField: this.createNewField,
      getDefaultField: this.fieldOperations.getDefaultField,
      generateGroup: this.generateGroup,
      buildFieldUpdate: this.fieldOperations.buildFieldUpdate,
      paste: this.paste,
      undo: this.undo,
      redo: this.redo,
      changeDefaultSigner: this.changeDefaultSigner,
      changeSigners: this.changeSigners,
      getSelectedFields: this.getSelectedFields,
      setPreviousFieldSnapshot: this.setPreviousFieldSnapshot,
      getPreviousFieldSnapshot: this.getPreviousFieldSnapshot,
      insertSignature: this.insertSignature,
      onContinue: this.onContinue,
      removeFields: this.removeFields,
      selectFields: this.selectFields,
      isFieldEditable: this.isFieldEditable,
      selectIntersectingFields: this.selectIntersectingFields,
      setNewComponentType: this.setNewComponentType,
      setPopoverOpen: this.setPopoverOpen,
      isValidAddress: this.isValidAddress,
      toggleKeyboardModal: this.toggleKeyboardModal,
      toggleReplaceDocumentModal: this.toggleReplaceDocumentModal,
      setReplaceSnapshotGuid: this.setReplaceSnapshotGuid,
      getReplaceSnapshotGuid: this.getReplaceSnapshotGuid,
      clearReplaceSnapshotGuid: this.clearReplaceSnapshotGuid,
      replaceData: this.props.replaceData,
      revertReplace: this.revertReplace,
      uploadFile: this.uploadFile,
      uploadFileFromIntegration: this.uploadFileFromIntegration,
      cancelDocumentUpload: this.cancelDocumentUpload,
      rejectFileUpload: this.rejectFileUpload,
      onDeleteDocument: this.onDeleteDocument,
      handlePageThumbnailClick: this.handlePageThumbnailClick,
      toggleHyperlinkField: this.toggleHyperlinkField,
      toggleMaskedField: this.toggleMaskedField,
      toggleSmallMenu: this.toggleSmallMenu,
      updateFields: this.updateFields,
      assignSelectedFields: this.assignSelectedFields,
      onCancel: this.onCancel,
      rotatePages: this.rotatePages,
      hideErrorBox: this.hideErrorBox,
      hideSuccessBox: this.hideSuccessBox,
      setSignatureRequestDateFormat: this.setSignatureRequestDateFormat,
      createNewRule: this.createNewRule,
      cancelRuleEdit: this.cancelRuleEdit,
      updateRule: this.updateRule,
      saveRule: this.saveRule,
      deleteRule: this.deleteRule,
      editRule: this.editRule,
      finishFieldsDeletion: this.finishFieldsDeletion,
      closeFieldDeletionModal: this.closeFieldDeletionModal,
      openEditSignersModal: this.openEditSignersModal,
      closeEditSignersModal: this.closeEditSignersModal,
      setView: this.setView,
      allowSender: this.allowSender,
      hidePreparer: this.hidePreparer,
      saveNumberOfClicks: this.saveNumberOfClicks,
      reorderFileSnapshots: this.props.reorderFileSnapshots,
      reorderDocuments: this.reorderDocuments,
    }, [this.state.windowWidth]);

    const nodes = (
      <editorContext.Provider value={context}>
        <RelayHeapEvents />
        <RemoteContinue onContinue={this.onContinue} />
        <RemoteAutoSave autoSave={this.autoSave} />
        <RemoteEditorValidation
          isUploading={isUploading}
          sendEditorMessage={this.props.sendEditorMessage} />
        <SignatureRequestContext
          dpi={this.props.dpi}
          ref={this.signatureRequestContextHack}
          pages={pages}
          fields={fields}
          selectedFieldIds={this.state.selectedFieldIds}
          defaultZoom={
            // For compatibility with one of the Storybook stories
            (this.props.initialState && this.props.initialState.zoom) || FIT_WIDTH
          }
          containerWidth={this.props.width}
          topGap={true}
          pageGap={10}
          defaultPageWidth={680}
        >
          {this.props.children}

          {this.state.confirmAssignSelectedFields && (
            <ConfirmModal
              onClose={() => this.setState({ confirmAssignSelectedFields: null })}
              {...this.state.confirmAssignSelectedFields} />
          )}
        </SignatureRequestContext>
      </editorContext.Provider>
    );

    // Strict mode in tests just adds noise
    if (process.env.NODE_ENV === 'test') {
      return nodes;
    } else {
      return <React.StrictMode>{nodes}</React.StrictMode>;
    }
  }
}

// WARNING: Do not try to wrap EditorContext. I know it's bad practice, but we
// have a bunch of tests that access the component directly. I think the right
// fix is to convert EditorContext to Redux actions/reducer.
export default EditorContext;
